import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS } from '../cardMesh';

// ── Generic recolourable card aura ───────────────────────────────────
//
// The same edge-hugging halo shader the Dragonlord ready glow uses,
// pulled out as a standalone helper so the Howl from Beyond reveal can
// paint a temporary face-up card with a harbinger-gold accent (a keeper)
// or a red warning (a banish). Unlike the Dragonlord glow this aura is
// NOT anchored to a live board card. The caller adds `aura.points`
// directly over the temporary reveal mesh and positions it itself, so
// the update path only breathes the pulse + ramps the fade-in.
//
// create / update / dispose trio, `.points` is the scene-root group.

const RISE_HEIGHT_OFFSET = 0.06;

const AURA_VERT = /* glsl */ `
  varying vec2 vLocalXY;
  void main() {
    vLocalXY = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const AURA_FRAG = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform float uStrength;
  uniform vec2  uCardSize;
  uniform vec3  uColor;

  varying vec2 vLocalXY;

  void main() {
    vec2 p = vLocalXY;
    vec2 q = abs(p) - uCardSize * 0.5;
    float sdf = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);

    float outer = 1.0 - smoothstep(0.0, uCardSize.x * 0.22, sdf);
    float inner = smoothstep(-uCardSize.x * 0.08, 0.0, sdf);
    float rim = outer * inner;

    float pulse = 0.7 + 0.3 * sin(uTime * 2.6);
    float intensity = rim * pulse * 0.8 * uStrength;

    if (intensity < 0.004) discard;
    gl_FragColor = vec4(uColor * intensity, intensity);
  }
`;

// Build a card-sized aura tinted `color` (a THREE.Color or hex number).
export function createCardAura(color) {
  const geometry = new THREE.PlaneGeometry(CARD_WIDTH * 1.7, CARD_HEIGHT * 1.55);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: Math.random() * 100 },
      uStrength: { value: 0 },
      uCardSize: { value: new THREE.Vector2(CARD_WIDTH, CARD_HEIGHT) },
      uColor: { value: new THREE.Color(color) },
    },
    vertexShader: AURA_VERT,
    fragmentShader: AURA_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  // Lay flat over the table-facing card and lift a hair above its art.
  mesh.rotation.x = -Math.PI / 2;
  mesh.renderOrder = 16;

  const group = new THREE.Group();
  group.frustumCulled = false;
  group.add(mesh);
  return { points: group, material, geometry };
}

// Follow a temp reveal mesh: copy its world XZ, sit just above it, breathe
// the pulse and ramp the fade-in. The reveal mesh lies flat (front-up), so
// the aura plane (already rotated -π/2) tracks its position only.
export function updateCardAura(aura, dt, cardMesh) {
  if (!aura) return;
  aura.material.uniforms.uTime.value += dt;
  const strength = aura.material.uniforms.uStrength;
  strength.value = Math.min(1, strength.value + dt * 3);
  if (cardMesh) {
    aura.points.position.set(
      cardMesh.position.x,
      cardMesh.position.y + CARD_THICKNESS / 2 + RISE_HEIGHT_OFFSET,
      cardMesh.position.z,
    );
  }
}

export function disposeCardAura(aura) {
  if (!aura) return;
  aura.points.parent?.remove(aura.points);
  aura.geometry?.dispose();
  aura.material?.dispose();
}
